package com.xorrus.trading.lot.relayclient.facade

import com.xorrus.trading.lot.relay.command.CommandErrorCode
import com.xorrus.trading.lot.relay.command.CommandResponse
import com.xorrus.trading.lot.relay.command.PushEvent
import com.xorrus.trading.lot.relay.engine.RequestOutcome
import com.xorrus.trading.lot.relayclient.LotCommands
import com.xorrus.trading.lot.relayclient.LotEvents
import com.xorrus.trading.lot.relayclient.model.ActivateArgs
import com.xorrus.trading.lot.relayclient.model.DesktopStatus
import com.xorrus.trading.lot.relayclient.model.FieldErrors
import com.xorrus.trading.lot.relayclient.model.LotJson
import com.xorrus.trading.lot.relayclient.model.PolicyDeniedTransport
import com.xorrus.trading.lot.relayclient.model.RemoteText
import com.xorrus.trading.lot.relayclient.model.RemoteStrategyStatus
import com.xorrus.trading.lot.relayclient.model.StrategyDescription
import com.xorrus.trading.lot.relayclient.model.StrategyId
import com.xorrus.trading.lot.relayclient.model.StrategyIdArgs
import com.xorrus.trading.lot.relayclient.model.StrategyListResult
import com.xorrus.trading.lot.relayclient.model.StrategyOptions
import com.xorrus.trading.lot.relayclient.model.StrategyParams
import com.xorrus.trading.lot.relayclient.model.StrategyStatusResult
import com.xorrus.trading.lot.relayclient.model.StrategySummary
import com.xorrus.trading.lot.relayclient.model.StrategyUpdate
import com.xorrus.trading.lot.relayclient.model.TransitionResult
import com.xorrus.trading.lot.relayclient.model.ValidationCode
import com.xorrus.trading.lot.relayclient.model.ValidationError
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.mapNotNull
import kotlinx.serialization.KSerializer
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.serializer

/**
 * Transport-Port der Fassade: in der SPA vom `ManagedControllerClient` der generischen
 * Lib bedient (jsMain), in Tests von Fakes/echten Engine-Sessions.
 */
interface RelayRequester {
    val events: Flow<PushEvent>
    suspend fun request(cmd: String, args: JsonObject = JsonObject(emptyMap())): RequestOutcome
}

/** Fehler eines Lese-Kommandos (`strategy.list`/`describe`/`status`). */
class LotCommandException(val code: String, val msg: String? = null) :
    Exception("$code${msg?.let { ": $it" } ?: ""}") {
    companion object {
        const val TIMEOUT = "TIMEOUT"
        const val SESSION_CLOSED = "SESSION_CLOSED"
    }
}

/** Ergebnis eines Übergangs aus Controller-Sicht (Spec §6). */
sealed interface TransitionOutcome {
    data class Applied(val status: RemoteStrategyStatus) : TransitionOutcome
    data class AlreadyInTargetState(val status: RemoteStrategyStatus) : TransitionOutcome

    /** Fehler-Response (`SCOPE_DENIED`/`POLICY_DENIED`/`BAD_ARGS`/… oder Host-`Rejected`). */
    data class Denied(
        val code: String,
        val msg: String? = null,
        /** Zweisprachige Fassung von [msg], sofern der Desktop sie liefert (Host-`Rejected`). */
        val msgText: RemoteText? = null,
    ) : TransitionOutcome

    /** `BAD_ARGS` mit Options-Mitgliedschaftsfehler ⇒ `describe` neu laden (§3.4). */
    data class StaleOptions(val fieldErrors: List<ValidationError>) : TransitionOutcome

    /**
     * Response-Drop (`Timeout`/`SessionClosed`): Ausführungsstatus unbekannt, KEIN Auto-Retry.
     * [lastStatus] stammt aus genau einem automatischen `strategy.status`-Read (§3.3/§6) —
     * `activeParams` darin zeigt, ob die Aktivierung durchkam.
     */
    data class Unknown(val lastStatus: RemoteStrategyStatus?) : TransitionOutcome
}

/** Host-`Rejected`-Ergebnisse werden als [TransitionOutcome.Denied] mit diesem Code gemeldet. */
const val TRANSITION_REJECTED = "REJECTED"

/**
 * SPA-Fassade (Spec §6): typisierte Sicht auf die LOT-Kommandos plus Event-Streams.
 * Lebt in commonMain, damit die Reconciliation-Logik auf jvm testbar ist; jsMain
 * exportiert einen schmalen JSON-Wrapper für die SPA.
 */
class LotController(private val relay: RelayRequester) {

    suspend fun strategies(): List<StrategySummary> =
        read(LotCommands.LIST, JsonObject(emptyMap()), StrategyListResult.serializer()).strategies

    suspend fun describe(id: StrategyId): StrategyDescription<StrategyParams, StrategyOptions> =
        read(LotCommands.DESCRIBE, idArgs(id), serializer())

    suspend fun status(id: StrategyId): RemoteStrategyStatus =
        read(LotCommands.STATUS, idArgs(id), StrategyStatusResult.serializer()).status

    /** Desktop-Zustand (TWS-Verbindung) — antwortet auch, wenn die Strategie-Anbindung fehlt. */
    suspend fun desktopStatus(): DesktopStatus =
        read(LotCommands.DESKTOP_STATUS, JsonObject(emptyMap()), DesktopStatus.serializer())

    /**
     * Read-only Vorschau (§-Erweiterung): rechnet mit [params] ein Ergebnis, ohne Zustand
     * zu ändern. Das Ergebnis-Schema ist strategie-spezifisch (z. B. `EgsPreview`) —
     * der Aufrufer dekodiert das rohe [JsonObject] typisiert. Wirft [LotCommandException]
     * bei nicht unterstützten Strategien (`BAD_ARGS`).
     */
    suspend fun preview(id: StrategyId, params: StrategyParams): JsonObject =
        read(
            LotCommands.PREVIEW,
            LotJson.encodeToJsonElement(ActivateArgs.serializer(), ActivateArgs(id, params)).jsonObject,
            JsonObject.serializer(),
        )

    suspend fun activate(id: StrategyId, params: StrategyParams): TransitionOutcome =
        transition(id, LotCommands.ACTIVATE, LotJson.encodeToJsonElement(ActivateArgs.serializer(), ActivateArgs(id, params)).jsonObject)

    suspend fun deactivate(id: StrategyId): TransitionOutcome =
        transition(id, LotCommands.DEACTIVATE, idArgs(id))

    /** Push-Stream der gedrosselten Status-Updates (§2). */
    fun updates(): Flow<StrategyUpdate> = relay.events.mapNotNull { event ->
        if (event.ev != LotEvents.STRATEGY_UPDATE) return@mapNotNull null
        try {
            LotJson.decodeFromJsonElement(StrategyUpdate.serializer(), event.data)
        } catch (_: Exception) {
            null // unbekannte Strategie/Version eines neueren Desktops — tolerant ignorieren
        }
    }

    /** Push-Stream des Desktop-Zustands (TWS-Verbindung). */
    fun desktopUpdates(): Flow<DesktopStatus> = relay.events.mapNotNull { event ->
        if (event.ev != LotEvents.DESKTOP_UPDATE) return@mapNotNull null
        try {
            LotJson.decodeFromJsonElement(DesktopStatus.serializer(), event.data)
        } catch (_: Exception) {
            null
        }
    }

    // ---- intern ------------------------------------------------------------------

    private fun idArgs(id: StrategyId): JsonObject =
        LotJson.encodeToJsonElement(StrategyIdArgs.serializer(), StrategyIdArgs(id)).jsonObject

    private suspend fun <R> read(cmd: String, args: JsonObject, serializer: KSerializer<R>): R =
        when (val outcome = relay.request(cmd, args)) {
            is RequestOutcome.Response -> {
                val response = outcome.response
                if (response.ok) {
                    LotJson.decodeFromJsonElement(serializer, response.result ?: JsonObject(emptyMap()))
                } else {
                    throw LotCommandException(response.error?.code ?: CommandErrorCode.INTERNAL, response.error?.msg)
                }
            }
            RequestOutcome.Timeout -> throw LotCommandException(LotCommandException.TIMEOUT)
            is RequestOutcome.SessionClosed -> throw LotCommandException(LotCommandException.SESSION_CLOSED, outcome.reason)
        }

    private suspend fun transition(id: StrategyId, cmd: String, args: JsonObject): TransitionOutcome =
        when (val outcome = relay.request(cmd, args)) {
            is RequestOutcome.Response -> mapResponse(outcome.response)
            // UNKNOWN: automatische Reconciliation per Status-Read, kein Auto-Retry des Übergangs (§3.3/§6)
            RequestOutcome.Timeout, is RequestOutcome.SessionClosed -> TransitionOutcome.Unknown(reconcileStatus(id))
        }

    /**
     * Reconciliation nach Response-Drop: Nach einer verlorenen Desktop-Antwort ist die
     * Session per reliable-or-dead (Protokoll-§10.2a) vergiftet — die Counter-Lücke tötet
     * den Kanal beim ersten weiteren Empfang. Der erste Read stirbt dann an
     * `SESSION_CLOSED`; der transparent neu verbindende Requester (ManagedControllerClient)
     * trägt genau EINEN zweiten Read-Versuch. Der Übergang selbst wird nie wiederholt.
     */
    private suspend fun reconcileStatus(id: StrategyId): RemoteStrategyStatus? {
        repeat(2) { attempt ->
            try {
                return status(id)
            } catch (e: LotCommandException) {
                if (e.code != LotCommandException.SESSION_CLOSED || attempt == 1) return null
            } catch (_: Exception) {
                return null
            }
        }
        return null
    }

    private fun mapResponse(response: CommandResponse): TransitionOutcome {
        if (response.ok) {
            val result = LotJson.decodeFromJsonElement(
                TransitionResult.serializer(),
                response.result ?: JsonObject(emptyMap()),
            )
            return when (result) {
                is TransitionResult.Applied -> TransitionOutcome.Applied(result.status)
                is TransitionResult.AlreadyInTargetState -> TransitionOutcome.AlreadyInTargetState(result.status)
                is TransitionResult.Rejected -> TransitionOutcome.Denied(TRANSITION_REJECTED, result.reason, result.reasonText)
            }
        }
        val code = response.error?.code ?: CommandErrorCode.INTERNAL
        val msg = response.error?.msg
        if (code == CommandErrorCode.BAD_ARGS) {
            val fieldErrors = FieldErrors.decodeOrNull(msg)
            if (fieldErrors != null && fieldErrors.any { it.code == ValidationCode.STALE_OPTION }) {
                return TransitionOutcome.StaleOptions(fieldErrors)
            }
        }
        if (code == CommandErrorCode.POLICY_DENIED) {
            PolicyDeniedTransport.decodeOrNull(msg)?.let { payload ->
                return TransitionOutcome.Denied(code, payload.message, payload.messageText)
            }
        }
        return TransitionOutcome.Denied(code, msg)
    }
}
